import { useCallback, useRef } from "react";
import {
  useUpdatePreOrderFlow,
  useCreatePreOrderFlow,
  useDeletePreOrderFlow,
} from "@/query/hooks/usePreOrders";
import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { ProductDefaults } from "../utils/resolveProductDefaults";

interface UseFlowHandlersOptions {
  preOrderId: string;
  /**
   * Optional optimistic updater for local flow state.
   * When provided, updates/creates/deletes are applied locally before the mutation fires.
   */
  setLocalFlows?: React.Dispatch<React.SetStateAction<PreOrderFlow[]>>;
}

/**
 * Shared stable callbacks for flow CRUD operations.
 * Uses refs to keep callbacks identity-stable across renders.
 */
export function useFlowHandlers({
  preOrderId,
  setLocalFlows,
}: UseFlowHandlersOptions) {
  const updateFlowMutation = useUpdatePreOrderFlow();
  const createFlowMutation = useCreatePreOrderFlow();
  const deleteFlowMutation = useDeletePreOrderFlow();

  // Stable refs so callbacks never change identity
  const updateFlowRef = useRef(updateFlowMutation);
  updateFlowRef.current = updateFlowMutation;
  const createFlowRef = useRef(createFlowMutation);
  createFlowRef.current = createFlowMutation;
  const deleteFlowRef = useRef(deleteFlowMutation);
  deleteFlowRef.current = deleteFlowMutation;
  const preOrderIdRef = useRef(preOrderId);
  preOrderIdRef.current = preOrderId;
  const setLocalFlowsRef = useRef(setLocalFlows);
  setLocalFlowsRef.current = setLocalFlows;

  const handleFlowUpdate = useCallback(
    (flowId: string, updates: Partial<PreOrderFlow>) => {
      // Optimistic local update when available
      setLocalFlowsRef.current?.((prev) =>
        prev.map((f) => (f.id === flowId ? { ...f, ...updates } : f)),
      );
      updateFlowRef.current.mutate({
        preOrderId: preOrderIdRef.current,
        flowId,
        data: updates,
      });
    },
    [],
  );

  const handleFlowCreate = useCallback((defaults: ProductDefaults) => {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const optimisticFlow = {
      id,
      pre_order_id: preOrderIdRef.current,
      product_id: defaults.product_id,
      product: defaults.product,
      quantity: 0,
      unit_id: defaults.unit_id,
      unit: defaults.unit,
      weight: defaults.weight ?? 0,
      price_per_weight: defaults.price_per_weight,
      comment: null,
      hidden_comment: null,
      status: 0,
      index: null,
      delivery_date: null,
      order_date: null,
      flow_metadata: null,
      created_at: now,
      updated_at: now,
    } as PreOrderFlow;
    // Optimistic local update when available
    setLocalFlowsRef.current?.((prev) => [...prev, optimisticFlow]);
    // Send complete flow to backend — same data used for optimistic cache
    createFlowRef.current.mutate({
      preOrderId: preOrderIdRef.current,
      flow: optimisticFlow,
    });
  }, []);

  const handleFlowDelete = useCallback((flowId: string) => {
    // Optimistic local update when available
    setLocalFlowsRef.current?.((prev) => prev.filter((f) => f.id !== flowId));
    deleteFlowRef.current.mutate({
      preOrderId: preOrderIdRef.current,
      flowId,
    });
  }, []);

  return { handleFlowUpdate, handleFlowCreate, handleFlowDelete };
}
